"use strict";
/** E-092 / D-002.032 — per-site CardConnect CoPilot boarding (fields not on site). */
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateSiteCardConnectForCreate = exports.validateOwnershipPercents = exports.parseAdditionalOwnersJson = exports.sanitizeAdditionalOwners = exports.emptySiteCardConnectInput = exports.isGatewayApiInfoAvailable = exports.GATEWAY_API_BOARDED_STATUS = exports.emptyAdditionalOwner = exports.splitOwnerName = exports.joinOwnerName = exports.toCopilotWebsite = exports.toCopilotBusinessPhone = exports.toCopilotDob = exports.BUSINESS_NAME_HINT = exports.BANK_ACCT_HINT = exports.BANK_ROUTING_HINT = exports.OWNERSHIP_MIN_HINT = exports.OWNER_AGE_HINT = exports.OWNER_DOB_HINT = exports.OWNER_ZIP_HINT = exports.OWNER_PHONE_HINT = exports.OWNER_EMAIL_HINT = exports.findDisallowedBusinessNameChars = exports.normalizeBusinessName = exports.formatBankNumber = exports.isValidBankAcctNum = exports.isValidBankRouting = exports.MIN_BENEFICIAL_OWNER_PCT = exports.BANK_ACCT_MAX_LENGTH = exports.BANK_ROUTING_LENGTH = exports.isAdultOwnerDob = exports.MIN_OWNER_AGE_YEARS = exports.isValidOwnerDob = exports.isValidOwnerEmail = exports.isValidOwnerZip = exports.formatOwnerZip = exports.isValidOwnerPhone = exports.normalizeOwnerPhone = exports.formatOwnerPhone = exports.BANK_ACCT_TYPE_OPTIONS = exports.OWNER_TITLE_OPTIONS = exports.OWNER_KYC_DISCLAIMER = exports.MAX_ADDITIONAL_OWNERS = void 0;
/** Primary owner + up to 3 additional owners (4 total). */
exports.MAX_ADDITIONAL_OWNERS = 3;
exports.OWNER_KYC_DISCLAIMER = "Please provide the following information for each individual who owns, directly or indirectly, 25% or more of the equity interest of your business, or who have significant responsibility to control, manage, or direct your business.";
/** Titles CoPilot accepts on ownership.owner.ownerTitle — `value` is sent as-is. */
exports.OWNER_TITLE_OPTIONS = [
    { id: 1, label: "CEO", value: "CEO" },
    { id: 2, label: "CFO", value: "CFO" },
    { id: 3, label: "COO", value: "COO" },
    { id: 4, label: "Member LLC", value: "MEMBER_LLC" },
    { id: 5, label: "Owner", value: "OWNER" },
    { id: 6, label: "Partner", value: "PARTNER" },
    { id: 7, label: "President", value: "PRESIDENT" },
    { id: 8, label: "Secretary", value: "SECRETARY" },
    { id: 9, label: "Treasurer", value: "TREASURER" },
    { id: 10, label: "Vice President", value: "VICE_PRESIDENT" }
];
/** CoPilot bankAcctTypeCd — `value` is sent as-is. */
exports.BANK_ACCT_TYPE_OPTIONS = [
    { id: 1, label: "Checking", value: "BIZ" },
    { id: 2, label: "Savings", value: "SAVINGS" }
];
/** CoPilot requires phones as "###-###-####" (max length 12). */
exports.formatOwnerPhone = (value) => {
    const digits = (value || "").replace(/\D+/g, "").slice(0, 10);
    if (digits.length <= 3)
        return digits;
    if (digits.length <= 6)
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};
/**
 * Reformat a stored phone for display, but only when it is unambiguously ten
 * digits — anything else is left alone so validation can flag it instead of
 * silently dropping digits.
 */
exports.normalizeOwnerPhone = (value) => {
    const digits = (value || "").replace(/\D+/g, "");
    return digits.length === 10 ? exports.formatOwnerPhone(digits) : value || "";
};
exports.isValidOwnerPhone = (value) => /^\d{3}-\d{3}-\d{4}$/.test(value || "");
exports.formatOwnerZip = (value) => (value || "").replace(/\D+/g, "").slice(0, 5);
exports.isValidOwnerZip = (value) => /^\d{5}$/.test(value || "");
exports.isValidOwnerEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((value || "").trim());
/** Owner date of birth is stored as ISO `YYYY-MM-DD`, like `patient.birthDate`. */
exports.isValidOwnerDob = (value) => {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || "").trim());
    if (!parts)
        return false;
    const year = Number(parts[1]);
    const month = Number(parts[2]);
    const day = Number(parts[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    const isRealDate = date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day;
    return isRealDate && date.getTime() <= Date.now();
};
/** CoPilot rejects owners under 18 (validation code 9823). */
exports.MIN_OWNER_AGE_YEARS = 18;
exports.isAdultOwnerDob = (value) => {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || "").trim());
    if (!parts || !exports.isValidOwnerDob(value))
        return false;
    const birthday = Date.UTC(Number(parts[1]) + exports.MIN_OWNER_AGE_YEARS, Number(parts[2]) - 1, Number(parts[3]));
    const today = new Date();
    return (birthday <=
        Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
};
/** CoPilot rejects routing numbers that are not exactly 9 digits (code 1501). */
exports.BANK_ROUTING_LENGTH = 9;
/** CoPilot rejects account numbers with symbols or over 17 digits (code 9854). */
exports.BANK_ACCT_MAX_LENGTH = 17;
/** CoPilot requires every listed beneficial owner to hold 25% or more (code 1144). */
exports.MIN_BENEFICIAL_OWNER_PCT = 25;
exports.isValidBankRouting = (value) => new RegExp(`^\\d{${exports.BANK_ROUTING_LENGTH}}$`).test((value || "").trim());
exports.isValidBankAcctNum = (value) => new RegExp(`^\\d{1,${exports.BANK_ACCT_MAX_LENGTH}}$`).test((value || "").trim());
exports.formatBankNumber = (value, maxLength) => (value || "").replace(/\D+/g, "").slice(0, maxLength);
/**
 * CoPilot rejects business names carrying any punctuation other than "&" and
 * "-" (validation code 9801). The rule covers legalBusinessName, dbaName, and
 * taxFilingName, and is enforced again at signature — after the merchant
 * exists and can no longer be edited from TMPP.
 */
const BUSINESS_NAME_ALLOWED_CHAR = /^[A-Za-z0-9 &-]$/;
/** CoPilot counts padding and repeated spaces against the field. */
exports.normalizeBusinessName = (value) => (value || "").replace(/\s+/g, " ").trim();
/** Offending characters, unique and in the order they appear. */
exports.findDisallowedBusinessNameChars = (value) => exports.normalizeBusinessName(value)
    .split("")
    .filter((char, index, chars) => !BUSINESS_NAME_ALLOWED_CHAR.test(char) && chars.indexOf(char) === index);
exports.OWNER_EMAIL_HINT = "Enter a valid email address";
exports.OWNER_PHONE_HINT = "Enter 10 digits (formatted as 123-456-7890)";
exports.OWNER_ZIP_HINT = "Enter a 5-digit ZIP code";
exports.OWNER_DOB_HINT = "Enter a past date as MM/DD/YYYY";
exports.OWNER_AGE_HINT = `Must be at least ${exports.MIN_OWNER_AGE_YEARS} years old`;
exports.OWNERSHIP_MIN_HINT = `Must hold at least ${exports.MIN_BENEFICIAL_OWNER_PCT}% ownership`;
exports.BANK_ROUTING_HINT = `Enter the ${exports.BANK_ROUTING_LENGTH}-digit routing number`;
exports.BANK_ACCT_HINT = `Enter up to ${exports.BANK_ACCT_MAX_LENGTH} digits, numbers only`;
exports.BUSINESS_NAME_HINT = 'Use letters, numbers, spaces, "&" and "-" only';
/** CoPilot expects ownerDob as MM/DD/YYYY (max length 10). */
exports.toCopilotDob = (value) => {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || "").trim());
    return parts ? `${parts[2]}/${parts[3]}/${parts[1]}` : "";
};
/**
 * CoPilot demographic.businessPhone is ###-###-#### (max 12). Details stores
 * values like "(731) 554-0571"; incomplete numbers become "" so CoPilot
 * does not reject an optional field.
 */
exports.toCopilotBusinessPhone = (value) => {
    const formatted = exports.formatOwnerPhone(value);
    return exports.isValidOwnerPhone(formatted) ? formatted : "";
};
/** CoPilot demographic.websiteAddress max 200; example is host without scheme. */
exports.toCopilotWebsite = (value) => (value || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .slice(0, 200);
/** CoPilot takes a single ownerName; the form collects first/last separately. */
exports.joinOwnerName = (firstName, lastName) => [(firstName || "").trim(), (lastName || "").trim()].filter(Boolean).join(" ");
exports.splitOwnerName = (ownerName) => {
    const trimmed = (ownerName || "").trim();
    if (!trimmed)
        return { firstName: "", lastName: "" };
    const at = trimmed.indexOf(" ");
    if (at < 0)
        return { firstName: trimmed, lastName: "" };
    return {
        firstName: trimmed.slice(0, at),
        lastName: trimmed.slice(at + 1).trim()
    };
};
exports.emptyAdditionalOwner = () => ({
    ownerFirstName: "",
    ownerLastName: "",
    ownerTitle: "",
    ownerEmail: "",
    ownerPhone: "",
    ownerAddress1: "",
    ownerAddress2: "",
    ownerCity: "",
    ownerState: "",
    ownerZip: "",
    ownerCountryCd: "US",
    ownershipPct: 0
});
/** Gateway boarding state that unlocks the Gateway API credential form. */
exports.GATEWAY_API_BOARDED_STATUS = "BOARDED";
/**
 * Credentials belong to a gateway MID, so they cannot be entered before
 * CardConnect assigns one and finishes gateway boarding. The Financial tab
 * button and the endpoints share this rule so they cannot drift apart.
 */
exports.isGatewayApiInfoAvailable = (gatewayMid, gatewayBoardingStatusCd) => !!(gatewayMid || "").trim() &&
    (gatewayBoardingStatusCd || "").toUpperCase() === exports.GATEWAY_API_BOARDED_STATUS;
exports.emptySiteCardConnectInput = (siteId) => ({
    siteId,
    ownerName: "",
    ownerTitle: "",
    ownerEmail: "",
    ownerPhone: "",
    ownerDob: "",
    ownerAddress1: "",
    ownerAddress2: "",
    ownerCity: "",
    ownerState: "",
    ownerZip: "",
    ownerCountryCd: "US",
    ownerDriversLicenseNumber: "",
    ownerDriversLicenseStateCd: "",
    ownerOwnershipPct: 100,
    additionalOwners: [],
    depositBankName: "",
    depositBankAcctTypeCd: "BIZ",
    depositBankAcctNum: "",
    depositBankRoutingNum: "",
    withdrawalSameAsDeposit: true,
    withdrawalBankName: "",
    withdrawalBankAcctTypeCd: "BIZ",
    withdrawalBankAcctNum: "",
    withdrawalBankRoutingNum: ""
});
const additionalOwnerPct = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};
exports.sanitizeAdditionalOwners = (owners) => {
    if (!Array.isArray(owners))
        return [];
    return owners.slice(0, exports.MAX_ADDITIONAL_OWNERS).map(owner => ({
        ownerFirstName: (owner && owner.ownerFirstName) || "",
        ownerLastName: (owner && owner.ownerLastName) || "",
        ownerTitle: (owner && owner.ownerTitle) || "",
        ownerEmail: (owner && owner.ownerEmail) || "",
        ownerPhone: (owner && owner.ownerPhone) || "",
        ownerAddress1: (owner && owner.ownerAddress1) || "",
        ownerAddress2: (owner && owner.ownerAddress2) || "",
        ownerCity: (owner && owner.ownerCity) || "",
        ownerState: (owner && owner.ownerState) || "",
        ownerZip: (owner && owner.ownerZip) || "",
        ownerCountryCd: (owner && owner.ownerCountryCd) || "US",
        ownershipPct: additionalOwnerPct(owner && owner.ownershipPct)
    }));
};
exports.parseAdditionalOwnersJson = (json) => {
    if (!json)
        return [];
    try {
        return exports.sanitizeAdditionalOwners(JSON.parse(json));
    }
    catch (_e) {
        return [];
    }
};
const requiredProblem = (label, missing) => missing.length
    ? [
        `${label}: ${missing.join(", ")} ${missing.length > 1 ? "are" : "is"} required.`
    ]
    : [];
const validateOwner = (owner) => {
    const missing = [];
    if (!owner.firstName.trim())
        missing.push("first name");
    if (!owner.lastName.trim())
        missing.push("last name");
    if (!owner.title)
        missing.push("title");
    if (!owner.email)
        missing.push("email");
    if (!owner.phone)
        missing.push("phone");
    if (!owner.address1)
        missing.push("address");
    if (!owner.city)
        missing.push("city");
    if (!owner.state)
        missing.push("state");
    if (!owner.zip)
        missing.push("ZIP");
    const problems = requiredProblem(owner.label, missing);
    if (owner.email && !exports.isValidOwnerEmail(owner.email)) {
        problems.push(`${owner.label}: ${exports.OWNER_EMAIL_HINT.toLowerCase()}.`);
    }
    if (owner.phone && !exports.isValidOwnerPhone(owner.phone)) {
        problems.push(`${owner.label}: ${exports.OWNER_PHONE_HINT.toLowerCase()}.`);
    }
    if (owner.zip && !exports.isValidOwnerZip(owner.zip)) {
        problems.push(`${owner.label}: ${exports.OWNER_ZIP_HINT.toLowerCase()}.`);
    }
    return problems;
};
const ownerPct = (value) => {
    const pct = Number(value);
    return Number.isFinite(pct) ? pct : 0;
};
exports.validateOwnershipPercents = (primaryPct, additionalOwners) => {
    const listed = [{ label: "Primary owner", pct: ownerPct(primaryPct) }].concat(additionalOwners.map((owner, index) => ({
        label: `Additional owner ${index + 1}`,
        pct: ownerPct(owner.ownershipPct)
    })));
    const problems = [];
    listed.forEach(entry => {
        if (entry.pct <= 0) {
            problems.push(`${entry.label}: ownership percent is required.`);
        }
        else if (additionalOwners.length &&
            entry.pct < exports.MIN_BENEFICIAL_OWNER_PCT) {
            problems.push(`${entry.label}: ${exports.OWNERSHIP_MIN_HINT.toLowerCase()}.`);
        }
    });
    if (additionalOwners.length) {
        const total = listed.reduce((sum, entry) => sum + entry.pct, 0);
        if (Math.round(total) !== 100) {
            problems.push(`Owner ownership percents must total 100% (currently ${total}%).`);
        }
    }
    return problems;
};
const validateBank = (label, bankName, acctNum, routingNum, hasStoredSecrets) => {
    const problems = [];
    if (!bankName)
        problems.push(`${label} bank: name is required.`);
    if (acctNum) {
        if (!exports.isValidBankAcctNum(acctNum)) {
            problems.push(`${label} bank account number: ${exports.BANK_ACCT_HINT.toLowerCase()}.`);
        }
    }
    else if (!hasStoredSecrets) {
        problems.push(`${label} bank: account number is required.`);
    }
    if (routingNum) {
        if (!exports.isValidBankRouting(routingNum)) {
            problems.push(`${label} bank routing number: ${exports.BANK_ROUTING_HINT.toLowerCase()}.`);
        }
    }
    else if (!hasStoredSecrets) {
        problems.push(`${label} bank: routing number is required.`);
    }
    return problems;
};
/**
 * Every rule CoPilot enforces on merchant create, checked before the call so a
 * rejected boarding attempt never reaches the gateway. Only owners currently on
 * the form are validated — removing one drops its problems with it.
 */
exports.validateSiteCardConnectForCreate = (input, stored = {}) => {
    const problems = [];
    const site = stored.site;
    if (site &&
        (!site.name ||
            !site.dba ||
            !site.address ||
            !site.city ||
            !site.state ||
            !site.zip)) {
        problems.push("Pharmacy Details (legal name, DBA, address) must be complete before creating a merchant.");
    }
    if (site) {
        [
            { label: "Pharmacy legal name", value: site.name },
            { label: "Pharmacy DBA", value: site.dba }
        ].forEach(field => {
            const rejected = exports.findDisallowedBusinessNameChars(field.value);
            if (rejected.length) {
                const listed = rejected.map(char => `"${char}"`).join(", ");
                problems.push(`${field.label} cannot contain ${listed}. ${exports.BUSINESS_NAME_HINT} — fix it on the Details tab.`);
            }
        });
    }
    const primaryName = exports.splitOwnerName(input.ownerName);
    problems.push(...validateOwner({
        label: "Primary owner",
        firstName: primaryName.firstName,
        lastName: primaryName.lastName,
        title: input.ownerTitle || "",
        email: input.ownerEmail || "",
        phone: input.ownerPhone || "",
        address1: input.ownerAddress1 || "",
        city: input.ownerCity || "",
        state: input.ownerState || "",
        zip: input.ownerZip || ""
    }));
    if (!input.ownerDob) {
        problems.push("Primary owner: date of birth is required.");
    }
    else if (!exports.isValidOwnerDob(input.ownerDob)) {
        problems.push(`Primary owner: ${exports.OWNER_DOB_HINT.toLowerCase()}.`);
    }
    else if (!exports.isAdultOwnerDob(input.ownerDob)) {
        problems.push(`Primary owner: ${exports.OWNER_AGE_HINT.toLowerCase()}.`);
    }
    if (!input.ownerDriversLicenseNumber && !stored.hasDriversLicense) {
        problems.push("Primary owner: driver's license number is required.");
    }
    if (!input.ownerDriversLicenseStateCd) {
        problems.push("Primary owner: driver's license state is required.");
    }
    const additionalOwners = input.additionalOwners || [];
    additionalOwners.forEach((owner, index) => {
        problems.push(...validateOwner({
            label: `Additional owner ${index + 1}`,
            firstName: owner.ownerFirstName || "",
            lastName: owner.ownerLastName || "",
            title: owner.ownerTitle || "",
            email: owner.ownerEmail || "",
            phone: owner.ownerPhone || "",
            address1: owner.ownerAddress1 || "",
            city: owner.ownerCity || "",
            state: owner.ownerState || "",
            zip: owner.ownerZip || ""
        }));
    });
    problems.push(...exports.validateOwnershipPercents(input.ownerOwnershipPct, additionalOwners));
    problems.push(...validateBank("Deposit", input.depositBankName || "", input.depositBankAcctNum, input.depositBankRoutingNum, !!stored.hasDepositBankSecrets));
    if (!input.withdrawalSameAsDeposit) {
        problems.push(...validateBank("Withdrawal", input.withdrawalBankName || "", input.withdrawalBankAcctNum, input.withdrawalBankRoutingNum, !!stored.hasWithdrawalBankSecrets));
    }
    return problems;
};



//////////////////
// WEBPACK FOOTER
// ./src/shared/site-cardconnect.ts
// module id = 4031
// module chunks = 0